import React from "react";
import { Box, CircularProgress, styled } from "@mui/material";
import TooltipCell from "controls/global/stewart-table/TooltipCell";
import { PremiumReceivablesTransactionRecord } from "entities/UIModel/PremiumReceivablesTransactionRecord";
import { PolicyHistoryDto } from "utils/api/policyHistoryApi";
import { colors, fontSize, fontWeight, padding } from "theme/defaultStyle";
import {
  formatCurrency,
  formatDate,
  formatRemitSplitPct,
} from "../../policy-management/shared/policyColumnHelpers";
import {
  PolicyLineItemsCache,
  usePolicyLineItems,
} from "./usePolicyLineItems";

// Premium Receivables policy line items (nested child rows).
//
// Expanding a REG Premium Receivables row reveals the policies behind it as
// child rows, indented under an L-shaped tree connector.
// Class names and CSS are carried over from the STNET1_React_v4_250 mockup
// (`pr-li-*`) so the two stay diff-able; the raw hex values are swapped for the
// nearest existing theme tokens rather than introducing a new palette.
//
// Unlike the mockup — where the children are sibling <tr>s in the parent grid
// and so line up under its columns — StewartTable renders the details row as a
// single cell spanning every column. This is therefore a self-contained table
// with its own header labels and widths, which also keeps it intact when the
// parent grid's columns are resized, hidden or reordered.
const Root = styled(Box)({
  backgroundColor: colors.grey33,
  "& table.details-table": {
    width: "100%",
    borderCollapse: "collapse",
    tableLayout: "fixed",
  },
  "& tr.pr-li-child > td": {
    backgroundColor: colors.grey33,
    color: colors.grey22,
    borderBottom: `1px solid ${colors.grey12}`,
    fontSize: fontSize.small1,
    padding: `${padding.small1} ${padding.medium1}`,
  },
  // Gentle zebra striping for readability across long policy lists.
  "& tr.pr-li-child.pr-li-alt > td": { backgroundColor: colors.grey34 },
  "& tr.pr-li-child:hover > td, & tr.pr-li-child.pr-li-alt:hover > td": {
    backgroundColor: colors.grey30,
  },
  // Identifier cell: indented to clear the tree, holds the connector and the
  // (emphasized) policy number. 36px sits just past the tick below.
  "& tr.pr-li-child td.pr-li-cell": {
    position: "relative",
    paddingLeft: "36px",
    whiteSpace: "nowrap",
  },
  // Vertical trunk down the whole branch; the LAST child's trunk stops at its
  // tick so the branch closes in an L.
  "& tr.pr-li-child td.pr-li-cell::before": {
    content: '""',
    position: "absolute",
    left: "16px",
    top: 0,
    bottom: 0,
    width: "1px",
    backgroundColor: colors.grey09,
  },
  "& tr.pr-li-child.pr-li-last td.pr-li-cell::before": { bottom: "50%" },
  // Horizontal tick out to the policy number.
  "& tr.pr-li-child td.pr-li-cell::after": {
    content: '""',
    position: "absolute",
    left: "16px",
    top: "50%",
    width: "14px",
    height: "1px",
    backgroundColor: colors.grey09,
  },
  "& tr.pr-li-child td.pr-li-cell .pr-li-pno": {
    fontWeight: fontWeight.bold2,
    color: colors.grey08,
  },
  // Description (form name) reads muted. `max-width: 0` is what lets the
  // ellipsis kick in inside a fixed-layout table.
  "& tr.pr-li-child td.pr-li-desc": {
    color: colors.grey22,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    maxWidth: 0,
  },
  "& tr.pr-li-child td.pr-li-sub": {
    color: colors.grey22,
    whiteSpace: "nowrap",
  },
  "& tr.pr-li-child td.pr-li-amt": { fontVariantNumeric: "tabular-nums" },
  "& tr.pr-li-child td.pr-li-amt-strong": {
    fontWeight: fontWeight.bold2,
    color: colors.grey08,
  },
  "& td.right": { textAlign: "right" },
  // Column labels for the nested line items — their meaning differs from the
  // parent grid's headers (Policy Number vs Agent ID, Risk Rate vs Remit
  // Basis, UW Remittance vs UW Retention).
  "& tr.pr-li-head-row > td": {
    backgroundColor: colors.grey37,
    color: colors.grey22,
    fontSize: fontSize.xsmall,
    fontWeight: fontWeight.bold2,
    letterSpacing: ".04em",
    textTransform: "uppercase",
    padding: `${padding.xsmall2} ${padding.medium1}`,
    borderBottom: `1px solid ${colors.grey17}`,
    whiteSpace: "nowrap",
  },
  // Line up the label over the indented policy numbers.
  "& tr.pr-li-head-row > td.pr-li-hnum": { paddingLeft: "36px" },
});

const StatusMessage = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: padding.small2,
  padding: `${padding.large1} ${padding.xlarge1}`,
  color: colors.grey22,
  fontSize: fontSize.small1,
});

// The mockup's split is a straight percentage on the policy; NewRemitSplitPct
// is the one in effect, falling back to the pre-adjustment value.
const splitPct = (dto: PolicyHistoryDto) =>
  formatRemitSplitPct(dto.NewRemitSplitPct ?? dto.OriginalRemitSplitPct);

interface PolicyLineItemsPanelProps {
  row: PremiumReceivablesTransactionRecord;
  userTimeZone: string;
  // Survives this component: StewartTable unmounts the drawer on collapse, so
  // without it every re-expand would refetch. Owned by TransactionsTable.
  cache?: PolicyLineItemsCache;
}

const PolicyLineItemsPanel = ({
  row,
  userTimeZone,
  cache,
}: PolicyLineItemsPanelProps) => {
  // Mounting *is* the "on expand" trigger — StewartTable only calls
  // renderRowDetails while the row is open — so the first expand's fetch starts
  // here. Later expands of the same row read the cache instead.
  const { records, isLoading } = usePolicyLineItems(row, userTimeZone, cache);

  if (isLoading) {
    return (
      <Root>
        <StatusMessage>
          <CircularProgress color="primary" size={16} />
          <span>Loading policies…</span>
        </StatusMessage>
      </Root>
    );
  }

  if (records.length === 0) {
    return (
      <Root>
        <StatusMessage>No policies found for this transaction.</StatusMessage>
      </Root>
    );
  }

  return (
    <Root>
      {/* `details-table` opts out of StewartTable's `width: calc(100% - 2px)`
          rule, which is scoped to `table:not(.details-table)`. */}
      <table className="details-table">
        <colgroup>
          <col style={{ width: "18%" }} />
          <col style={{ width: "20%" }} />
          <col style={{ width: "9%" }} />
          <col style={{ width: "10%" }} />
          <col style={{ width: "8%" }} />
          <col style={{ width: "9%" }} />
          <col style={{ width: "9%" }} />
          <col style={{ width: "8%" }} />
          <col style={{ width: "9%" }} />
        </colgroup>
        <tbody>
          <tr className="pr-li-head-row">
            <td className="pr-li-hnum">Policy Number</td>
            <td>Policy Form Name</td>
            <td>Policy Date</td>
            <td>County</td>
            <td className="right">Split %</td>
            <td className="right">Liability</td>
            <td className="right">Gross Premium</td>
            <td className="right">Risk Rate</td>
            <td className="right">UW Remittance</td>
          </tr>
          {records.map((dto, index) => {
            // Zebra on odd rows; the final row's connector closes into an L.
            const alt = index % 2 === 1 ? " pr-li-alt" : "";
            const last = index === records.length - 1 ? " pr-li-last" : "";
            return (
              <tr
                key={String(dto.PolicyHistoryID)}
                className={`pr-li-child${alt}${last}`}
              >
                <td className="pr-li-cell">
                  <span className="pr-li-pno">{dto.PolicyNumber || ""}</span>
                </td>
                <td className="pr-li-desc">
                  <TooltipCell title={dto.PolicyFormName || ""} />
                </td>
                <td className="pr-li-sub">{formatDate(dto.EffectiveDate)}</td>
                <td className="pr-li-sub">{dto.CountyDisplayName || ""}</td>
                <td className="pr-li-sub right">{splitPct(dto)}</td>
                <td className="right pr-li-amt">
                  {formatCurrency(dto.LiabilityAmount)}
                </td>
                <td className="right pr-li-amt">
                  {formatCurrency(dto.ActualFee)}
                </td>
                <td className="right pr-li-amt">
                  {formatCurrency(dto.ActualRiskRate)}
                </td>
                <td className="right pr-li-amt pr-li-amt-strong">
                  {formatCurrency(dto.ActualRemittance)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </Root>
  );
};

export default PolicyLineItemsPanel;
